"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, PiggyBank, Zap, ShieldCheck, TrendingUp, MessageSquare, Check, X, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const benefitsList = [{
  id: 1,
  title: "Direct Access to Me",
  consultant: "You work 100% directly with me, Yassine. No account managers, middle management, or passing off tasks.",
  agency: "You talk to account executives, while your actual SEO work is delegated to rotating junior staff.",
  iconName: "UserCheck",
  pinkHighlight: "100% Senior Attention"
}, {
  id: 2,
  title: "Zero Overhead Costs",
  consultant: "Every pound of your retainer goes into high-impact strategy and execution, not corporate overhead.",
  agency: "Up to 50% of your retainer subsidizes office space, sales teams, and agency profit margins.",
  iconName: "PiggyBank",
  pinkHighlight: "Maximum ROI Focus"
}, {
  id: 3,
  title: "Bespoke, Tailored Strategy",
  consultant: "I custom-build every strategy around your precise business model, market position, and conversion goals.",
  agency: "Standardized templates, automated audit tools, and checklist SEO applied to every client.",
  iconName: "Sparkles",
  pinkHighlight: "Custom SEO Architecture"
}, {
  id: 4,
  title: "Agile & Fast Execution",
  consultant: "Direct communication means immediate strategy tweaks, rapid technical fixes, and fast turnarounds.",
  agency: "Requests wait in long ticketing queues and require multiple internal sign-offs before implementation.",
  iconName: "Zap",
  pinkHighlight: "Immediate Implementation"
}, {
  id: 5,
  title: "Full Transparency & Honesty",
  consultant: "I give you honest assessments, realistic timelines, and clear reporting focused on business revenue.",
  agency: "Vague monthly reports filled with vanity metrics to obscure lack of genuine organic growth.",
  iconName: "ShieldCheck",
  pinkHighlight: "Honest Metric Tracking"
}, {
  id: 6,
  title: "Selective Client Load",
  consultant: "I limit the number of clients I accept so I can give your campaign the dedicated focus it requires.",
  agency: "Agencies push account managers to handle 15-20+ clients simultaneously, diluting quality.",
  iconName: "TrendingUp",
  pinkHighlight: "Dedicated Focus"
}];
export default function Consultantbenefits() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "UserCheck":
        return <UserCheck className="size-5 text-primary" />;
      case "PiggyBank":
        return <PiggyBank className="size-5 text-primary" />;
      case "Sparkles":
        return <Sparkles className="size-5 text-accent" />;
      case "Zap":
        return <Zap className="size-5 text-primary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-5 text-primary" />;
      case "TrendingUp":
        return <TrendingUp className="size-5 text-primary" />;
      default:
        return <MessageSquare className="size-5 text-primary" />;
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border/50">
      <div className="max-w-7xl mx-auto space-y-16">
        
        {}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="secondary" className="bg-secondary text-secondary-foreground border-accent/20 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase">
            <Sparkles className="size-3.5 mr-1 text-accent inline-block" />
            Direct Partnership Model
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Why Work Directly With Me Instead of an Agency?
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg font-default leading-relaxed">
            When you partner with YE Consultancy, you get my hands-on SEO expertise, direct accountability, and a tailored growth roadmap. No agency fluff, no junior delegation—just genuine SEO growth.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {benefitsList.map((item, i) => <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.4,
          delay: i * 0.08
        }} className="h-full">
              <Card className="h-full bg-card border-border hover:border-accent/40 transition-[border-color,box-shadow] duration-300 shadow-sm hover:shadow-md flex flex-col justify-between overflow-hidden">
                <CardHeader className="space-y-3 pb-4">
                  <div className="flex items-center justify-between">
                    <div className="p-2.5 rounded-lg bg-muted inline-flex items-center justify-center">
                      {getIcon(item.iconName)}
                    </div>
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary text-foreground/80 border border-accent/20">
                      {item.pinkHighlight}
                    </span>
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground font-default pt-2">
                    {item.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4 flex-1">
                  {}
                  <div className="p-3.5 rounded-lg bg-secondary/50 border border-accent/20 space-y-1.5">
                    <div className="flex items-center gap-2 font-medium text-xs text-accent-foreground font-default uppercase tracking-wider">
                      <Check className="size-4 text-accent shrink-0" />
                      <span>Working With Me</span>
                    </div>
                    <p className="text-sm text-foreground/90 font-default leading-relaxed pl-6">
                      {item.consultant}
                    </p>
                  </div>

                  {}
                  <div className="p-3.5 rounded-lg bg-muted/60 border border-border/60 space-y-1.5 opacity-80">
                    <div className="flex items-center gap-2 font-medium text-xs text-muted-foreground font-default uppercase tracking-wider">
                      <X className="size-4 text-destructive/80 shrink-0" />
                      <span>Traditional SEO Agency</span>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground font-default leading-relaxed pl-6">
                      {item.agency}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {}
        <div className="bg-card border border-border rounded-2xl p-8 sm:p-10 shadow-sm flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="space-y-2 text-center md:text-left max-w-2xl">
            <h3 className="text-2xl font-semibold text-foreground font-default">
              Ready for a transparent, high-converting SEO strategy?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base font-default">
              Skip the middleman and get straightforward advice on how to scale your organic search traffic, leads, and revenue.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium px-6">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}